import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Rss, Check, Copy, Download, Mail, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface LicenseInfo {
  licenseKey: string;
  email: string;
  purchasedAt: string;
}

export default function PurchaseSuccess() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [license, setLicense] = useState<LicenseInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchLicense() {
      const sessionId = searchParams.get('session_id');

      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/stripe/session/${sessionId}`);
        const data = await response.json();

        if (response.ok) {
          setLicense(data);
        } else {
          setError(data.error || 'Failed to retrieve license');
        }
      } catch (err) {
        setError('Failed to connect to server');
      } finally {
        setLoading(false);
      }
    }

    fetchLicense();
  }, [searchParams]);

  const copyToClipboard = () => {
    if (license) {
      navigator.clipboard.writeText(license.licenseKey);
      toast.success('License key copied!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-accent-600 animate-spin mx-auto mb-4" />
          <p className="text-ink-muted">Loading your license...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-canvas rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">!</span>
          </div>
          <h1 className="text-2xl font-bold text-ink mb-2">Something went wrong</h1>
          <p className="text-ink-muted mb-6">{error}</p>
          <p className="text-sm text-ink-muted">
            If you completed a purchase, check your email for the license key.
            If you need help, contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-canvas border-b border-edge">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent-600 rounded-lg flex items-center justify-center">
              <Rss className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-ink">OurSS</h1>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Success Message */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-ink mb-2">Thank you for your purchase!</h1>
          <p className="text-ink-muted">Your OurSS license is ready.</p>
        </div>

        {/* License Key Card */}
        <div className="bg-canvas rounded-2xl shadow-lg border border-edge overflow-hidden mb-8">
          <div className="p-6 bg-gray-50 border-b border-edge">
            <h2 className="text-lg font-semibold text-ink">Your License Key</h2>
            <p className="text-sm text-ink-muted">Keep this safe - you'll need it for installation</p>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <code className="flex-1 px-4 py-3 bg-gray-100 rounded-lg font-mono text-lg text-ink select-all">
                {license?.licenseKey}
              </code>
              <button
                onClick={copyToClipboard}
                className="p-3 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copy to clipboard"
              >
                <Copy className="w-5 h-5 text-ink-muted" />
              </button>
            </div>
            <div className="flex items-center gap-2 text-sm text-ink-muted">
              <Mail className="w-4 h-4" />
              <span>Also sent to: {license?.email}</span>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-canvas rounded-2xl shadow-lg border border-edge p-6 mb-8">
          <h2 className="text-lg font-semibold text-ink mb-4">Next Steps</h2>
          <ol className="space-y-4">
            <li className="flex gap-4">
              <span className="flex-shrink-0 w-8 h-8 bg-accent-100 text-accent-700 rounded-full flex items-center justify-center font-semibold">1</span>
              <div>
                <h3 className="font-medium text-ink">Download OurSS</h3>
                <p className="text-ink-muted text-sm">Get the latest version from your download page</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="flex-shrink-0 w-8 h-8 bg-accent-100 text-accent-700 rounded-full flex items-center justify-center font-semibold">2</span>
              <div>
                <h3 className="font-medium text-ink">Set up your server</h3>
                <p className="text-ink-muted text-sm">Run the installer or use Docker</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="flex-shrink-0 w-8 h-8 bg-accent-100 text-accent-700 rounded-full flex items-center justify-center font-semibold">3</span>
              <div>
                <h3 className="font-medium text-ink">Enter your license key</h3>
                <p className="text-ink-muted text-sm">Add it to your .env file or during setup</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="flex-shrink-0 w-8 h-8 bg-accent-100 text-accent-700 rounded-full flex items-center justify-center font-semibold">4</span>
              <div>
                <h3 className="font-medium text-ink">Start reading!</h3>
                <p className="text-ink-muted text-sm">Your self-hosted RSS reader is ready</p>
              </div>
            </li>
          </ol>
        </div>

        {/* Download Button */}
        <div className="text-center">
          <Link
            to={`/download/${license?.licenseKey}`}
            className="inline-flex items-center gap-2 px-8 py-4 bg-accent-600 hover:bg-accent-700 text-white font-semibold rounded-lg transition-colors"
          >
            <Download className="w-5 h-5" />
            Go to Download Page
          </Link>
          <p className="text-sm text-ink-muted mt-4">
            You can always access your download page with your license key
          </p>
        </div>
      </div>
    </div>
  );
}
